/**
 * appConfig.ts
 *
 * v2.1.4
 *
 * © --- Gruppo Insieme S.r.l.
 * v2.1.4 --- 20/08/2025 --- ADDED FEATURE SECTIONS
 * v2.1.3 --- 05/08/2025 --- ADDED NEW CORPORATE DATA SECTION IN UI
 * v2.1.2 --- 24/07/2025 --- REFINED WITH NEW OPTIONS AND CLEANED COMMENTS
 * v2.1.1 --- 22/04/2025 --- REFINED WITH SPECIFIC JSON STRUCTURE
 * v2.1.0 --- 22/04/2025 --- REFACTORED VERSION
 * v2.0.0 --- 11/04/2025 --- SECOND RELEASE
 * v1.0.0 --- 07/04/2025 --- FIRST RELEASE
 *
 * Central configuration file for the real estate agency website frontend.
 **/

import agencyFieldsData from './agencyFields.json';

// --- Specific Interfaces from JSON ---

type MarkerData = Array<object>

interface CenterMapData {
    center_lat?: number;
    center_lng?: number;
    zoom?: number;
    snazzyWindow?: boolean;
}

interface PartnerData {
    url: string;
    imgSrc: string;
    imgAlt: string;
}

interface WorkWithUsImage {
    width: string;
    height: string;
}

interface ServicesTextItem {
    image?: string;
    imageAlt?: string;
    bullet1?: string;
    bullet2?: string;
    bullet3?: string;
    bullet4?: string;
}

interface ServicesTextData {
    image1?: ServicesTextItem;
    image2?: ServicesTextItem;
    subParagraph?: {
        firstRow?: string;
        secondRow?: string;
    };
}

interface AboutUsData {
    images?: string[];
}

interface HolidayPopupJson {
    enabled?: boolean;
    startDate?: string;
    endDate?: string;
    imageUrl?: string;
    title?: string;
    greetingText?: string;
    closureText?: string;
    reopeningText?: string;
}

interface HomeValuationJson {
    enabled?: boolean;
    displayMode?: 'banner' | 'popup';
    title?: string;
    subtitle?: string;
    agentName?: string;
    agentPhotoUrl?: string;
    agentEmail?: string;
    agentPhone?: string;
}

// --- Interfaccia per agencyFields.json (Riflette la struttura del JSON) ---
interface AgencyFieldsJson {
    agencyId?: number | string;
    realizationYear?: number | string;
    agencyName?: string;
    agencyNameLegal?: string;
    agencyEmail?: string;
    contactEmail?: string;
    intlPrefix?: string;
    customMobile?: string;
    customMobile2?: string;
    customPhone?: string;
    customPhone2?: string;
    customAddress?: string;
    mapMarkerColor?: string;
    markerData?: MarkerData;
    centerMapData?: CenterMapData;
    partners?: PartnerData[];
    aboutUsSection?: AboutUsData;
    workWithUsPage?: {
        image: WorkWithUsImage
    };
    servicesText?: ServicesTextData;
    siteKeyRecaptchaV2?: string;
    siteKeyRecaptchaV3?: string;
    colors?: {
        primaryBlue?: string;
        primaryBlueHover?: string;
        accentGold?: string;
        accentGoldHover?: string;
        calloutOrange?: string;
        darkBlueBase?: string;
    };
    iubenda?: {
        siteId?: number | string;
        cookiePolicyId?: number | string; // Presente nel JSON ma useremo _it, _en, _pt
        cookiePolicyId_it?: number | string;
        cookiePolicyId_en?: number | string;
        cookiePolicyId_pt?: number | string;
        apiKey?: string;
    };
    agencyDefault?: {
        defaultAgencyName?: string;
        defaultPhone1?: string;
        defaultMobile?: string;
        defaultIntlPrefix?: string;
    };
    agencySocialLinks?: {
        facebook?: string;
        youtube?: string;
        instagram?: string;
        pinterest?: string;
        whatsapp?: string;
        twitter?: string;
        linkedin?: string;
        tiktok?: string;
        threads?: string;
    };
    header?: {
        backgroundColor?: string;
        textColor?: string;
    };
    immobiliPage?: {
        defaultView?: 'grid' | 'list';
        showViewToggle?: boolean;
    };
    holidayPopup?: HolidayPopupJson;
    homeValuation?: HomeValuationJson;
}

// --- Interfaccia per l'Oggetto appConfig Finale Consolidato ---
interface AppConfig {
    agency: {
        id: number;
        realizationYear: string;
        name: string;
        nameLegal: string;
        contactEmail: string;
        agencyEmail: string;
        intlPrefix: string;
        customMobile: string;
        customMobile2: string;
        customPhone: string;
        customPhone2: string;
        customAddress: string;
        defaults: {
            name: string;
            phone1: string;
            mobile: string;
            intlPrefix: string;
        };
    };
    theme: {
        colors: {
            primaryBlue: string;
            primaryBlueHover: string;
            accentGold: string;
            accentGoldHover: string;
            calloutOrange: string;
            darkBlueBase: string;
        };
        header: {
            backgroundColor: string;
            textColor: string;
        };
    };
    integrations: {
        recaptcha: {
            v2Key: string;
            v3Key: string;
        };
        iubenda: {
            siteId: number;
            policyId: {
                it: number;
                en: number;
                pt: number;
            };
            apiKey: string;
            showBanner: boolean;
        };
        multilang: boolean;
    };
    socialLinks: {
        facebook: string;
        youtube: string;
        instagram: string;
        pinterest: string;
        whatsapp: string;
        twitter: string;
        linkedin: string;
        tiktok: string;
        threads: string;
    };
    map: {
        markerColor: string;
        defaultCenter: { lat: number; lng: number };
        defaultZoom: number;
        markerData: MarkerData | null;
        centerMapData: CenterMapData | null;
    };
    content: {
        aboutUsSection: AboutUsData | null;
        servicesText: ServicesTextData | null;
        partners: PartnerData[];
    };
    ui: {
        header: {
            showLanguageSelector: boolean;
        };
        showWhatsAppButton: boolean;
        routes: {
            showBlogRoutes: boolean;
            showSuccessRoutes: boolean;
        };
        homePage: {
            agencyName: string;
            seo: boolean;
            vetrina2: boolean;
            showServices: boolean;
            pageType: "home";
            filters: {
                tipologia: string[];
                zona: string[];
                prezzo: string[];
            };
            showSearchAndFilters: boolean;
            showLocationSearch: boolean;
            showLastArticles: boolean;
            showFooterBannerVendi: boolean;
            showPresentationSection: boolean;
            showSocialSection: boolean;
            showVetrina1: boolean;
        };
        immobiliPage: {
            defaultView: 'grid' | 'list'; // Letto da JSON con fallback
            showViewToggle: boolean;
        };
        workWithUsPage: {
            image: WorkWithUsImage;
        };
        agencyWidget: {
            showEmail: boolean;
            showSocialLinks: boolean;
            showPhone: boolean;
            showMobile: boolean;
        };
        searchForm: {
            showSearchInHomePageSubHeader: boolean;
            showSearchBelowHomePageIntro: boolean;
            showPriceSlider: boolean;
        };
        contactUsPage: {
            showMap: boolean;
        };
        footer: {
            showTel1: boolean;
            showTel2: boolean;
            showMobile1: boolean;
            showMobile2: boolean;
            showEmail: boolean;
            showExternalLink: boolean;
        };
        corporateData: {
            showRegisteredOffice: boolean;
            showVat: boolean;
            showFiscalCode: boolean;
            showRea: boolean;
            showPec: boolean;
            showSocialCapital: boolean;
        };
    };
    constants: { // Costanti logiche hardcoded
        propertyCategories: {
            residential: number;
            plot: number;
        };
        propertyTypes: {
            garage: number;
        };
    };
    features: {
        propertySharing: {
            enabled: boolean,
            whatsapp: boolean,
            facebook: boolean,
            email: boolean,
        };
        printablePropertyCard: {
            enabled: boolean,
        };
        mortgageSimulation: {
            enabled: boolean,
        };
        googleReviews: {
            enabled: boolean,
            placeId: string,
            displayType: string,
            numberOfReviews: number,
        };
        holidayPopup: {
            enabled: boolean;
            startDate: string;
            endDate: string;
            imageUrl: string;
            title: string;
            greetingText: string;
            closureText: string;
            reopeningText: string;
        };
        homeValuation: {
            enabled: boolean;
            displayMode: 'banner' | 'popup';
            title: string;
            subtitle: string;
            agentName: string;
            agentPhotoUrl: string;
            agentEmail: string;
            agentPhone: string;
        };
        vacationRentals: {
            enabled: boolean;
        };
        propertyDetailMap: {
            enabled: boolean;
        };
        verifiedPropertyBadge: {
            enabled: boolean;
        };
        breadcrumbs: {
            enabled: boolean;
        };
        panoramicPhotos: {
            pannellum: {
                enabled: boolean;
            };
            photoSphere: {
                enabled: boolean;
            };
        };
    };
}

// --- Helper Functions ---
function safeNumber(value: string | number | null | undefined, fallback: number): number {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value === 'number') return isNaN(value) ? fallback : value;
    const num = Number(value);
    return isNaN(num) ? fallback : num;
}

function safeString(value: string | number | boolean | null | undefined, fallback: string = ''): string {
    if (value === null || value === undefined) return fallback;
    return String(value);
}

// --- Default Values ---
const DEFAULT_COLORS = {
    primaryBlue: "",
    primaryBlueHover: "",
    accentGold: "",
    accentGoldHover: "",
    calloutOrange: "",
    darkBlueBase: "",
};
const DEFAULT_HEADER_BG = DEFAULT_COLORS.darkBlueBase;
const DEFAULT_HEADER_TEXT = "";

const DEFAULT_MAP_CENTER = { lat: 0, lng: 0 };
const DEFAULT_MAP_ZOOM = 12;
const DEFAULT_PROPERTY_CATEGORIES = { residential: 1, plot: 4 };
const DEFAULT_PROPERTY_TYPES = { garage: 4 };

// --- Type Cast agencyFieldsData ---
const agencyData = agencyFieldsData as AgencyFieldsJson;

// --- Build the Configuration Object ---
export const appConfig: AppConfig = {
    agency: {
        id: safeNumber(agencyData.agencyId, 0),
        realizationYear: safeString(agencyData.realizationYear, ""),
        name: safeString(agencyData.agencyName, "Agenzia Immobiliare"),
        nameLegal: safeString(agencyData.agencyNameLegal, safeString(agencyData.agencyName, "Agenzia Immobiliare")),
        contactEmail: safeString(agencyData.contactEmail),
        agencyEmail: safeString(agencyData.agencyEmail),
        intlPrefix: safeString(agencyData.intlPrefix, "+39"),
        customMobile: safeString(agencyData.customMobile, ""),
        customMobile2: safeString(agencyData.customMobile2, ""),
        customPhone: safeString(agencyData.customPhone, ""),
        customPhone2: safeString(agencyData.customPhone2, ""),
        customAddress: safeString(agencyData.customAddress, ""),
        defaults: {
            name: safeString(agencyData.agencyDefault?.defaultAgencyName, "Agenzia Immobiliare"),
            phone1: safeString(agencyData.agencyDefault?.defaultPhone1, "N/A"),
            mobile: safeString(agencyData.agencyDefault?.defaultMobile, "N/A"),
            intlPrefix: safeString(agencyData.agencyDefault?.defaultIntlPrefix, "+39"),
        },
    },
    theme: {
        colors: {
            primaryBlue: safeString(agencyData.colors?.primaryBlue, DEFAULT_COLORS.primaryBlue),
            primaryBlueHover: safeString(agencyData.colors?.primaryBlueHover, DEFAULT_COLORS.primaryBlueHover),
            accentGold: safeString(agencyData.colors?.accentGold, DEFAULT_COLORS.accentGold),
            accentGoldHover: safeString(agencyData.colors?.accentGoldHover, DEFAULT_COLORS.accentGoldHover),
            calloutOrange: safeString(agencyData.colors?.calloutOrange, DEFAULT_COLORS.calloutOrange),
            darkBlueBase: safeString(agencyData.colors?.darkBlueBase, DEFAULT_COLORS.darkBlueBase),
        },
        header: {
            backgroundColor: safeString(agencyData.header?.backgroundColor, DEFAULT_HEADER_BG),
            textColor: safeString(agencyData.header?.textColor, DEFAULT_HEADER_TEXT),
        },
    },
    integrations: {
        recaptcha: {
            v2Key: safeString(agencyData.siteKeyRecaptchaV2), // Mappato v2
            v3Key: safeString(agencyData.siteKeyRecaptchaV3), // Mappato v3
        },
        iubenda: {
            siteId: safeNumber(agencyData.iubenda?.siteId, 0),
            policyId: { // Mappa dalle chiavi specifiche per lingua
                it: safeNumber(agencyData.iubenda?.cookiePolicyId_it, 0),
                en: safeNumber(agencyData.iubenda?.cookiePolicyId_en, 0),
                pt: safeNumber(agencyData.iubenda?.cookiePolicyId_pt, 0),
            },
            apiKey: safeString(agencyData.iubenda?.apiKey),
            showBanner: true,
        },
        multilang: false,
    },
    socialLinks: {
        facebook: safeString(agencyData.agencySocialLinks?.facebook),
        youtube: safeString(agencyData.agencySocialLinks?.youtube),
        instagram: safeString(agencyData.agencySocialLinks?.instagram),
        pinterest: safeString(agencyData.agencySocialLinks?.pinterest),
        whatsapp: safeString(agencyData.agencySocialLinks?.whatsapp),
        twitter: safeString(agencyData.agencySocialLinks?.twitter),
        linkedin: safeString(agencyData.agencySocialLinks?.linkedin),
        tiktok: safeString(agencyData.agencySocialLinks?.tiktok),
        threads: safeString(agencyData.agencySocialLinks?.threads),
    },
    map: {
        markerColor: safeString(agencyData.mapMarkerColor, DEFAULT_COLORS.primaryBlue), // Letto da JSON con fallback
        defaultCenter: DEFAULT_MAP_CENTER,
        defaultZoom: DEFAULT_MAP_ZOOM,
        markerData: agencyData.markerData ?? null,
        centerMapData: agencyData.centerMapData ?? null,
    },
    content: {
        aboutUsSection: agencyData.aboutUsSection ?? null,
        servicesText: agencyData.servicesText ?? null,
        partners: agencyData.partners ?? [],
    },
    ui: { // Configurazioni UI, alcune hardcoded, altre leggono da JSON
        header: {
            showLanguageSelector: false,
        },
        showWhatsAppButton: false,
        routes: {
            showBlogRoutes: false,
            showSuccessRoutes: false,
        },
        homePage: {
            agencyName: "Laura Mastalli Immobiliare",
            seo: true,
            vetrina2: true,
            pageType: "home",
            filters: {
                tipologia: ["1", "15", "73"],
                zona: ["1", "2", "3"],
                prezzo: ["1", "2", "3"],
            },
            showServices: false,
            showSearchAndFilters: true,
            showLocationSearch: false,
            showLastArticles: false,
            showFooterBannerVendi: false,
            showPresentationSection: false,
            showSocialSection: false,
            showVetrina1: false,
        },
        immobiliPage: { // Letto da JSON con fallback 'grid'
            defaultView: agencyData.immobiliPage?.defaultView === 'list' ? 'list' : 'grid',
            showViewToggle: agencyData.immobiliPage?.showViewToggle ?? false,
        },
        workWithUsPage: {
            image: {
                height: agencyData.workWithUsPage?.image.height || "458.8",
                width: agencyData.workWithUsPage?.image.width || "228.817",
            }
        },
        agencyWidget: {
            showEmail: true,
            showSocialLinks: true,
            showPhone: true,
            showMobile: true
        },
        searchForm: {
            showSearchInHomePageSubHeader: false,
            showSearchBelowHomePageIntro: true,
            showPriceSlider: false,
        },
        contactUsPage: {
            showMap: true,
        },
        footer: {
            showTel1: false,
            showTel2: true,
            showMobile1: true,
            showMobile2: true,
            showEmail: true,
            showExternalLink: false
        },
        corporateData: {
            showRegisteredOffice: true,
            showVat: true,
            showFiscalCode: true,
            showRea: true,
            showPec: true,
            showSocialCapital: true,
        }
    },
    constants: {
        propertyCategories: DEFAULT_PROPERTY_CATEGORIES,
        propertyTypes: DEFAULT_PROPERTY_TYPES,
    },
    features: {
        propertySharing: { // CONDIVISIONE IMMOBILE (BOTTONI)
            enabled: false, // Attiva/disattiva l'intera sezione di condivisione
            whatsapp: false,
            facebook: false,
            email: false,
        },
        printablePropertyCard: { // FUNZIONE STAMPA SCHEDA IMMOBILE
            enabled: false, // Attiva/disattiva la funzione di stampa
        },
        mortgageSimulation: { // SIMULAZIONE RATA MUTUO 
            enabled: false, // Attiva/disattiva la richiesta di simulazione mutuo
        },
        googleReviews: { // INSERIRE LE RECENSIONI GOOGLE
            enabled: false, // Attiva/disattiva la sezione recensioni
            placeId: '', // Place ID dell'agenzia immobiliare su google maps
            // Per trovare place ID https://developers.google.com/maps/documentation/places/web-service/place-id?hl=it
            displayType: 'best', // Opzioni: 'latest' o 'best'
            numberOfReviews: 5,
        },
        holidayPopup: { // POPUP DI BUONE FESTE CON MSG DI ORARI DI CHIUSURA
            enabled: agencyData.holidayPopup?.enabled ?? false,
            startDate: safeString(agencyData.holidayPopup?.startDate, '2025-12-20'),    // YYYY-MM-DD
            endDate: safeString(agencyData.holidayPopup?.endDate, '2026-01-01'),        // YYYY-MM-DD
            imageUrl: safeString(agencyData.holidayPopup?.imageUrl, ''),
            title: safeString(agencyData.holidayPopup?.title, 'Buone Feste!'),
            greetingText: safeString(agencyData.holidayPopup?.greetingText, ''),
            closureText: safeString(agencyData.holidayPopup?.closureText, ''),
            reopeningText: safeString(agencyData.holidayPopup?.reopeningText, ''),
        },
        homeValuation: { // COMPONENTE DENTRO "HOME" PER VALUTARE CASA
            enabled: agencyData.homeValuation?.enabled ?? false,
            displayMode: agencyData.homeValuation?.displayMode === 'popup' ? 'popup' : 'banner',
            title: safeString(agencyData.homeValuation?.title, 'Valuta il tuo immobile'),
            subtitle: safeString(agencyData.homeValuation?.subtitle, 'Richiedi una valutazione gratuita.'),
            agentName: safeString(agencyData.homeValuation?.agentName, ''),
            agentPhotoUrl: safeString(agencyData.homeValuation?.agentPhotoUrl, ''),
            agentEmail: safeString(agencyData.homeValuation?.agentEmail, ''),
            agentPhone: safeString(agencyData.homeValuation?.agentPhone, ''),
        },
        vacationRentals: { // PER ABILITARE ANCHE LE INFORMAZIONI AGGIUNTIVE PER GLI AFFITTI VACANZA
            enabled: false,
        },
        propertyDetailMap: { // MAPPA DI DETTAGLIO DELL'IMMOBILE NELLA RELATIVA SCHEDA
            enabled: false,
        },
        verifiedPropertyBadge: { // PER METTERE IL BADGE "IMMOBILE VERIFICATO" CON IL LOGO CASACHECK24
            enabled: false,
        },
        breadcrumbs: { // PER ATTIVARE IL MENÙ DI NAVIGAZIONE "A BRICIOLE" PER UNA BACK-NAV PIÙ EFFICIENTE E VELOCE
            enabled: false,
        },
        panoramicPhotos: { // PER ATTIVARE LA VISUALIZZAZIONE DELLE FOTO PANORAMICHE
            pannellum: { // NAVIGAZIONE INTORNO CON SOLO LE ARROW KEYS (LAYOUT 1)
                enabled: false,
            },
            photoSphere: { // NAVIGAZIONE INTERNO CON ARROW KEYS, MOUSE E TOUCHPAN. FULLSCREEN MODE TRIGGERABILE 
                enabled: false, // LAYOUT MOLTO MIGLIORE DEL PRIMO.
            },
        },
    },
};

// --- Runtime Debug Checks ---
if (!appConfig.agency.id || appConfig.agency.id === 0) {
    console.warn("Attenzione: ID Agenzia (appConfig.agency.id) non trovato, non valido o impostato al valore di fallback (0).");
}
if (!appConfig.integrations.iubenda.siteId || !appConfig.integrations.iubenda.policyId.it) {
    console.warn("Attenzione: Configurazione Iubenda (siteId o policyId.it) mancante o non valida in appConfig.");
}
if (!appConfig.integrations.recaptcha.v3Key) {
    console.warn("Attenzione: Site Key di reCAPTCHA v3 (appConfig.integrations.recaptcha.v3Key) mancante.");
}
if (!appConfig.agency.contactEmail) {
    console.warn("Attenzione: Email di contatto (appConfig.agency.contactEmail) mancante.");
}
if (!['grid', 'list'].includes(appConfig.ui.immobiliPage.defaultView)) {
    console.warn(`Attenzione: Configurazione 'defaultView' (appConfig.ui.immobiliPage.defaultView) non valida. Valore: '${appConfig.ui.immobiliPage.defaultView}'. Usato fallback 'grid'.`);
}


// --- Esportazioni Individuali per Compatibilità/Convenienza ---

export const AGENCY_ID = Number(appConfig.agency.id);
export const REALIZATION_YEAR = appConfig.agency.realizationYear;
export const AGENCY_NAME = appConfig.agency.name;
export const AGENCY_NAME_LEGAL = appConfig.agency.nameLegal;
export const AGENCY_CONTACT_EMAIL = appConfig.agency.contactEmail;
export const DEFAULT_AGENCY_NAME = appConfig.agency.defaults.name;
export const DEFAULT_PHONE_1 = appConfig.agency.defaults.phone1;
export const DEFAULT_MOBILE = appConfig.agency.defaults.mobile;
export const DEFAULT_INTL_PREFIX = appConfig.agency.defaults.intlPrefix;

export const COLOR_PRIMARY_BLUE = appConfig.theme.colors.primaryBlue;
export const COLOR_PRIMARY_BLUE_HOVER = appConfig.theme.colors.primaryBlueHover;
export const COLOR_ACCENT_GOLD = appConfig.theme.colors.accentGold;
export const COLOR_ACCENT_GOLD_HOVER = appConfig.theme.colors.accentGoldHover;
export const COLOR_CALLOUT_ORANGE = appConfig.theme.colors.calloutOrange;
export const COLOR_DARK_BLUE_BASE = appConfig.theme.colors.darkBlueBase;
export const HEADER_BACKGROUND_COLOR = appConfig.theme.header.backgroundColor;
export const HEADER_TEXT_COLOR = appConfig.theme.header.textColor;

export const RECAPTCHA_V2_SITE_KEY = appConfig.integrations.recaptcha.v2Key;
export const RECAPTCHA_V3_SITE_KEY = appConfig.integrations.recaptcha.v3Key;

export const RECAPTCHA_SITE_KEY = appConfig.integrations.recaptcha.v3Key;

export const IUBENDA_SITE_ID = appConfig.integrations.iubenda.siteId;
export const IUBENDA_POLICY_ID_IT = appConfig.integrations.iubenda.policyId.it;
export const IUBENDA_POLICY_ID_EN = appConfig.integrations.iubenda.policyId.en;
export const IUBENDA_POLICY_ID_PT = appConfig.integrations.iubenda.policyId.pt;
export const IUBENDA_API_KEY = appConfig.integrations.iubenda.apiKey;

export const FACEBOOK_LINK = appConfig.socialLinks.facebook;
export const YOUTUBE_LINK = appConfig.socialLinks.youtube;
export const INSTAGRAM_LINK = appConfig.socialLinks.instagram;
export const PINTEREST_LINK = appConfig.socialLinks.pinterest;
export const WHATSAPP_LINK = appConfig.socialLinks.whatsapp;
export const TWITTER_LINK = appConfig.socialLinks.twitter;
export const LINKEDIN_LINK = appConfig.socialLinks.linkedin;

export const MAP_MARKER_COLOR = appConfig.map.markerColor;
export const DEFAULT_CENTER_LAT = appConfig.map.defaultCenter.lat;
export const DEFAULT_CENTER_LNG = appConfig.map.defaultCenter.lng;
export const DEFAULT_ZOOM = appConfig.map.defaultZoom;

export const PARTNERS_DATA = appConfig.content.partners;

export const RESIDENTIAL_CTG_ID = appConfig.constants.propertyCategories.residential;
export const PLOT_CTG_ID = appConfig.constants.propertyCategories.plot;
export const GARAGE_TYPE_ID = appConfig.constants.propertyTypes.garage;

export const IMMOBILI_DEFAULT_VIEW = appConfig.ui.immobiliPage.defaultView;
export const IMMOBILI_SHOW_VIEW_TOGGLE = appConfig.ui.immobiliPage.showViewToggle;

// --- Export Principale ---
export default appConfig;